import { useEffect, useState } from 'react';
import { getDoc, getDocs, doc, collection } from 'firebase/firestore';
import { useDispatch } from 'react-redux';
import { db, auth } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { userLoadedAction } from '../../redux/actions/user';
import { useSelector } from 'react-redux';

export const loadUser = async ({
  source,
  user,
  selectedOrganisationId,
  registrationInProgress = false,
  dispatch,
  setFirestoreUserLoaded = () => {}
}) => {
  if (
    auth.currentUser &&
    user &&
    !user.validUser &&
    !user.invalidUser &&
    !registrationInProgress
  ) {
    const userSnapshot = await getDoc(doc(db, 'users', user.uid));

    if (!userSnapshot.exists()) {
      dispatch({
        type: 'INVALID_USER',
        payload: {}
      });
      signOut(auth)
        .then(() => {
          // Sign-out successful.
        })
        .catch(error => {
          console.log(error);
        });

      return;
    }

    let userDoc = userSnapshot.data();

    const userOrganisationsSnap = await getDocs(
      collection(db, `users/${user.uid}/user_organisations`)
    );
    const userOrganisations = userOrganisationsSnap.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));

    const authorisedRolesSnap = await getDocs(
      collection(db, 'authorised_roles')
    );
    const authorisedRoles = authorisedRolesSnap.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));

    if (
      (auth.currentUser && ['owner', 'admin'].some(r => r === userDoc.role)) ||
      authorisedRoles.some(a => a.role === userDoc.role && a.enablePortalLogin)
    ) {
      await user.getIdToken(true);
      const decodedToken = await auth.currentUser.getIdTokenResult();

      let userOrgId = selectedOrganisationId;
      if (!userOrgId && userOrganisations.length === 1) {
        // default to the current organisation if user only has one, otherwise we will be asking them to choose which organisation they want to login to
        userOrgId = userOrganisations[0].organisationId;
      }

      const payload = {
        source: `useAuthenticated.${source}`,
        organisations: userOrganisations,
        selectedOrganisationId: userOrgId,
        ...userDoc,
        session: {
          accessToken: user?.accessToken,
          displayName: user?.displayName,
          photoURL: user?.photoURL,
          uid: user?.uid,
          tenantId: user?.tenantId,
          providerId: user?.providerId,
          email: user?.email,
          emailVerified: user?.emailVerified,
          phoneNumber: user?.phoneNumber,
          phoneVerified: user?.phoneVerified
        },
        claims: decodedToken?.claims || []
      } || {
        error: `could not find user with uid ${user.uid}`
      };

      if (payload?.session.auth) {
        // non-serializble
        delete payload.session.auth;
      }

      dispatch(userLoadedAction(payload));
    } else {
      dispatch({
        type: 'INVALID_USER',
        payload: {}
      });
      signOut(auth)
        .then(() => {
          // Sign-out successful.
        })
        .catch(error => {
          console.log(error);
        });
    }

    setFirestoreUserLoaded(true);
  }
};

const useAuthenticated = source => {
  const [user, loading] = useAuthState(auth);

  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);
  const [firestoreUserLoaded, setFirestoreUserLoaded] = useState(false);
  const local = useSelector(state => state.Local);
  const userDoc = useSelector(state => state.User);

  useEffect(() => {
    if (!userDoc?.validUser) {
      loadUser({
        source,
        user,
        selectedOrganisationId: userDoc?.selectedOrganisationId,
        dispatch,
        registrationInProgress: local?.registrationInProgress,
        setFirestoreUserLoaded
      });
    }
  }, [user, local?.registrationInProgress]);

  useEffect(() => {
    if (!loading && !user) {
      setReady(true);
    } else if (!loading && user && firestoreUserLoaded) {
      setReady(true);
    } else if (!loading && user && local?.registrationInProgress) {
      setReady(true);
    }
  }, [loading, firestoreUserLoaded]);

  return {
    ready
  };
};

export default useAuthenticated;
